import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, X } from 'lucide-react';
import { rfqAPI, clientAPI, configAPI } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/i18n/translations';
import { formatDate } from '@/lib/utils';
import { formatApiError } from '@/lib/apiError';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, statusBadgeVariant } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const STATUSES = [
  'in_preparation',
  'in_analysis',
  'awaiting_logistics',
  'awaiting_commercial_margins',
  'awaiting_finance',
  'awaiting_direction',
  'approved',
  'offer_sent',
  'won',
  'lost',
  'in_negotiation',
];

export default function RFQListPage() {
  const { lang, user } = useAuth();
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [alloyFilter, setAlloyFilter] = useState('all');
  const [machineFilter, setMachineFilter] = useState('all');
  const [clients, setClients] = useState([]);
  const [alloys, setAlloys] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load filter dimensions once. Errors are silent: filters simply stay empty.
  useEffect(() => {
    Promise.all([
      clientAPI.list().catch(() => ({ data: [] })),
      configAPI.getAlloys().catch(() => ({ data: [] })),
      configAPI.getMachines().catch(() => ({ data: [] })),
    ]).then(([c, a, m]) => {
      setClients(c.data || []);
      setAlloys(a.data || []);
      setMachines(m.data || []);
    });
  }, []);

  const loadRFQs = () => {
    setLoading(true);
    rfqAPI
      .list({
        page,
        page_size: pageSize,
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        client_id: clientFilter === 'all' ? undefined : clientFilter,
        alloy_code: alloyFilter === 'all' ? undefined : alloyFilter,
        machine_id: machineFilter === 'all' ? undefined : machineFilter,
      })
      .then((r) => {
        setRfqs(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRFQs();
  }, [page, pageSize, statusFilter, clientFilter, alloyFilter, machineFilter]); // eslint-disable-line

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const hasActiveFilters =
    statusFilter !== 'all' ||
    clientFilter !== 'all' ||
    alloyFilter !== 'all' ||
    machineFilter !== 'all' ||
    search !== '';

  const clearFilters = () => {
    setStatusFilter('all');
    setClientFilter('all');
    setAlloyFilter('all');
    setMachineFilter('all');
    setSearch('');
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadRFQs();
  };

  const canDelete = (rfq) =>
    rfq.status === 'in_preparation' && user && (user.is_admin || rfq.created_by === user.id);

  const handleDelete = async (rfq, e) => {
    e.stopPropagation();
    const confirmMsg = t('delete_rfq_confirm', lang).replace('{ref}', rfq.reference_number);
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) return;
    try {
      await rfqAPI.remove(rfq.id);
      toast.success(t('rfq_deleted_toast', lang));
      loadRFQs();
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        kicker={t('app_title', lang)}
        title={t('rfqs', lang)}
        actions={(
          <Button onClick={() => navigate('/rfqs/new')}>
            <Plus className="h-4 w-4" />
            {t('new_rfq', lang)}
          </Button>
        )}
      />

      <Card>
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('search', lang)}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_states', lang)}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(s, lang)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={clientFilter}
            onValueChange={(v) => {
              setClientFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('all_clients', lang)} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_clients', lang)}</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={alloyFilter}
            onValueChange={(v) => {
              setAlloyFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('all_alloys', lang)} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_alloys', lang)}</SelectItem>
              {alloys.map((a) => (
                <SelectItem key={a.id ?? a.code} value={a.code}>
                  {a.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={machineFilter}
            onValueChange={(v) => {
              setMachineFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('all_machines', lang)} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_machines', lang)}</SelectItem>
              {machines.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.name || m.code || `#${m.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit" variant="outline">
            {t('search', lang)}
          </Button>
          {hasActiveFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" /> {t('clear_filters', lang)}
            </Button>
          )}
          <span className="ml-auto text-sm text-muted-foreground">{total} {t('results', lang)}</span>
        </form>
      </Card>

      <Card className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : rfqs.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('no_rfqs_found', lang)}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reference', lang)}</TableHead>
                <TableHead>{t('client', lang)}</TableHead>
                <TableHead>{t('part_designation', lang)}</TableHead>
                <TableHead>{t('status', lang)}</TableHead>
                <TableHead>{t('deadline', lang)}</TableHead>
                <TableHead>{t('created_at', lang)}</TableHead>
                <TableHead className="w-[56px] text-right" aria-label={t('actions', lang)} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rfqs.map((rfq) => (
                <TableRow
                  key={rfq.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/rfqs/${rfq.id}`)}
                >
                  <TableCell>
                    <span className="font-semibold text-primary">{rfq.reference_number}</span>
                  </TableCell>
                  <TableCell>{rfq.client?.name || '—'}</TableCell>
                  <TableCell>{rfq.part_designation}</TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(rfq.status)}>{t(rfq.status, lang)}</Badge>
                  </TableCell>
                  <TableCell>{formatDate(rfq.deadline, lang)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(rfq.created_at, lang)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canDelete(rfq) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => handleDelete(rfq, e)}
                        aria-label={t('delete', lang)}
                        title={t('delete', lang)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('per_page', lang)}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[90px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" /> {t('previous', lang)}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('page_x_of_y', lang)
              .replace('{page}', String(page))
              .replace('{pages}', String(totalPages))}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t('next', lang)} <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
